import React, { useState, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faFileArchive, faInfoCircle } from '@fortawesome/pro-solid-svg-icons'
import Uppy from '@uppy/core'
import AwsS3Multipart from '@uppy/aws-s3-multipart'
import { Input, Tooltip } from 'antd'
import Button from 'design-system/button'
import { ButtonVariant, ButtonSizes } from 'design-system/button/src/Button'
import AwesomeFileInput from 'components/ui/forms/field/awesomeFileInput/AwesomeFileInput'
import { UploadFileStatusEnum } from 'enum/uploadFile-status.enum'
import { IMainState } from 'store/states'
import * as fromActions from 'store/actions/blocks.action'
import { ErrorToast } from 'components/ui/toast/toastsTemplates'
import { getS3FileId } from 'helpers/converters.helper'
import ConfirmationModal from 'components/ui/modals/confirmation/ConfirmationModal'
import { gbToBytes } from 'helpers/others.helper'
import Wrapper from 'components/reusables/wrapper'
import { getInvalidEntry } from './utils'
import Configuration from './configuration'
import { Subtitle, Actions } from './styled'

export interface IScormData {
  name: string
  description: string
  file: File | {}
  showTitle: boolean
  openNewWindow: boolean
  blockAdvance: boolean
}

interface IScorm {
  onCancel: any
  editing: boolean
}

const defaultValues: IScormData = {
  name: '',
  description: '',
  file: {},
  showTitle: true,
  openNewWindow: false,
  blockAdvance: true,
}

// Restricciones
const ONE_GB = gbToBytes(1)

const Scorm: React.FC<IScorm> = ({ onCancel, editing }) => {
  const { t } = useTranslation()
  const translationPrefix = 'administration.blocks.type.scorm'
  const dispatch = useDispatch()

  const [data, setData] = useState(defaultValues)
  const [filesStatus, setFileStatus] = useState([] as any)
  const [endedFile, setEndedFile] = useState({} as any)
  const [errorFile, setErrorFile] = useState({} as any)
  const [newChange, setNewChange] = useState(false)
  const [filesArray, setFilesArray] = useState({} as any)
  const [, setAnswers] = useState([] as any)
  const [uploadingScorm, setUploadingScorm] = useState(false)

  const [showConfirmationModal, setShowConfirmationModal] = useState(false)
  const [confirmModalAction, setConfirmModalAction] = useState()
  // Referencias usadas para mantener la data actualizada en todo el componente
  const ref = useRef({} as any)
  const cancel = useRef('')

  // Se encarga de intercambiar los ids de los archivos generados por uppy por los asignados por el back al crear la respuesta
  const { ids } = useSelector(({ answer }: IMainState) => answer)
  useEffect(() => {
    if (ids.length > 0) {
      const auxId = ids.map(({ id, uppyId }: any) => {
        if (ref.current[uppyId]) {
          const aux = { ...ref.current[uppyId], id: id }
          delete ref.current[uppyId]
          ref.current[id] = aux
        }
        return id
      })
      setNewChange(true)
      setAnswers((current) => [...current, ...auxId])
    }
  }, [ids])

  // Previene que el usuario recarge o cambie de página cuando un archivo aun se esté cargando
  const preventPageReload = (event: any) => {
    for (const id in ref.current) {
      if (ref.current[id].status === UploadFileStatusEnum.inProcess) {
        event.preventDefault()
        event.returnValue = true
      }
    }
  }
  useEffect(() => {
    window.addEventListener('beforeunload', preventPageReload)
    return () => {
      window.removeEventListener('beforeunload', preventPageReload)
    }
  }, [])

  // Cargar datos existes
  const { edit } = useSelector(({ blocks }: IMainState) => blocks)
  useEffect(() => {
    if (edit) {
      const {
        name,
        description,
        showTitle,
        file,
        openNewWindow,
        blockAdvance,
      } = edit
      setData({
        ...defaultValues,
        name,
        description,
        ...(showTitle !== undefined && { showTitle: showTitle }),
        ...(openNewWindow !== undefined && { openNewWindow: openNewWindow }),
        ...(blockAdvance !== undefined && { blockAdvance: blockAdvance }),
      })
      ref.current = {}
      const ids = [] as any
      if (file && file.id) {
        const {
          id,
          metadata: { size, filename, mimeType },
          url,
        } = file
        ref.current[id] = {
          id,
          name: filename,
          type: mimeType,
          size,
          progress: 0,
          status: UploadFileStatusEnum.completed,
          download: url,
        }
        id && ids.push(id)
      }
      setAnswers(ids)
      setNewChange(true)
    }
  }, [edit])

  // Guardar
  const handleOnSave = () => {
    const { id, lessonId } = edit
    let file: any = null
    const hasFiles = !!filesStatus.length

    let result = { ...data, id, lessonId }
    for (const id in ref.current) {
      const currentFile = ref.current[id]
      if (getS3FileId(currentFile.download)) {
        file = {
          id: getS3FileId(currentFile.download),
          storage: 'cache',
          metadata: {
            uppy_id: id,
            filename: currentFile.name,
            mime_type: currentFile.type,
            size: currentFile.size,
          },
        }
        result = { ...result, file }
      }
    }
    // @ts-ignore
    if (hasFiles && !result.file.id) {
      const loadedFile = filesStatus[0]
      const idFile = loadedFile.id
      file = {
        id: idFile,
        storage: 'store',
        metadata: {
          uppy_id: id,
          filename: loadedFile.name,
          mime_type: loadedFile.type,
          size: loadedFile.size,
        },
      }
      result = { ...result, file }
    }
    dispatch({ type: fromActions.EDIT_SCORM_BLOCK, payload: result })
    onCancel()
  }

  const handleContinue = (action: any) => {
    let canContinue = true
    for (const id in ref.current) {
      if (ref.current[id].status === UploadFileStatusEnum.inProcess) {
        canContinue = false
        setShowConfirmationModal(true)
        setConfirmModalAction(() => action)
      }
    }
    canContinue && action()
  }

  // Inicio de la instancia de uppy 💀
  const uppy = Uppy({
    restrictions: {
      maxFileSize: ONE_GB,
    },
  }).use(AwsS3Multipart, {
    companionUrl: `//${process.env.REACT_APP_API_URL}`,
  })

  // Valida que el nombre del archivo no se repita
  const isTheSameFile = (filename: string) => {
    for (const id in ref.current) {
      if (ref.current[id].name === filename) return true
    }
    return false
  }

  // Se selecciona un archivo (arrastrar o seleccionar)
  const onSelectFiles = async (files: File[]) => {
    if (files && files.length > 0) {
      const { file, entry } = { ...(await getInvalidEntry(files)) }
      if (entry && file) {
        ErrorToast(
          t(`lessons.contentBlocks.taskBlock.toast.invalidScorm`, {
            file: file.name,
            name: entry?.entryName,
          }),
          { autoClose: 20000 }
        )
        return
      }

      files.map((file) => {
        if (!isTheSameFile(file.name)) {
          if (file.size < ONE_GB) {
            try {
              uppy.addFile({
                id: '',
                source: 'taskBlock',
                name: file.name,
                type: file.type,
                data: file,
              })
              setFilesArray((current: any) => ({
                ...current,
                [file.name]: file,
              }))
            } catch (err) {
              ErrorToast(
                t(`lessons.contentBlocks.taskBlock.toast.uploadProblems`)
              )
            }
          } else {
            ErrorToast(t(`lessons.contentBlocks.taskBlock.toast.sizeExceeded`))
          }
        } else {
          ErrorToast(t(`lessons.contentBlocks.taskBlock.toast.sameName`))
        }
      })
      setUploadingScorm(true)
      uppy.upload().finally(() => {
        setUploadingScorm(false)
      })
    }
  }

  // Comienza la carga
  uppy.on('upload', () => {
    const toDelete = Object.keys(ref.current)[0]
    cancel.current = toDelete
    ref.current = {}
  })
  // Actualiza el porcentaje de carga (una instancia de socket por cada archivo)
  uppy.on(
    'upload-progress',
    ({ id, name, size, type }, { bytesUploaded, bytesTotal }) => {
      // @ts-ignore
      const percentage = (bytesUploaded / bytesTotal).toFixed(2) * 100
      if (id !== cancel.current) {
        ref.current[id] = {
          id,
          name,
          size,
          type,
          progress: percentage,
          status: UploadFileStatusEnum.inProcess,
        }
        setNewChange(true)
      }
      if (cancel.current != '') {
        uppy.getFile(cancel.current) && uppy.removeFile(cancel.current)
      }
    }
  )
  // Termina la carga (una instancia de socket por cada archivo)
  uppy.on('upload-success', ({ id, name, size, type }, { uploadURL }) => {
    const result = {} as any
    result[id] = {
      id,
      name,
      size,
      progress: 100,
      type,
      status: UploadFileStatusEnum.completed,
      download: uploadURL,
    }
    setEndedFile(result)
  })

  // Despues de cancelar la carga de un archivo, uppy lanza este evento
  uppy.on('file-removed', ({ id }) => {
    if (cancel.current === id) {
      cancel.current = ''
    }
  })

  // Cuando uppy destecta algún error en la carga (solo probado con errores de conexión)
  uppy.on('upload-error', ({ id, name, size }, error) => {
    const result = {} as any
    result[id] = {
      id,
      name,
      size,
      progress: 0,
      status: UploadFileStatusEnum.error,
      error,
    }
    setErrorFile(result)
  })

  // Si un archivo termina su carga (terminó de cargar o tuvo errores), se actualiza el estado principal
  useEffect(() => {
    if (Object.values(endedFile).length > 0) {
      for (const id in endedFile) {
        if (ref.current[id]) {
          if (!ref.current[id].download) {
            ref.current[id] = endedFile[id]
            setNewChange(true)
          }
        }
      }
    }
    if (Object.values(errorFile).length > 0) {
      for (const id in errorFile) {
        if (ref.current[id] && !ref.current[id].error) {
          ref.current[id] = errorFile[id]
          setNewChange(true)
        }
      }
    }
  }, [endedFile, errorFile])

  // Cada vez que un archivo entra en estado de error, se puede lanzar un retry, que vuelve a cargar el archivo desde cero
  const handleRetry = (name: any, id: string) => {
    try {
      const file = filesArray[name]
      if (file) {
        uppy.addFile({
          source: 'taskBlock',
          name: file.name,
          type: file.type,
          data: file,
        })
        uppy.upload()
        setFilesArray((current: any) => ({ ...current, [file.name]: file }))
        delete ref.current[id]
        setNewChange(true)
      }
    } catch (error) {
      ErrorToast(t(`lessons.contentBlocks.taskBlock.toast.uploadProblems`))
    }
  }

  // Cancelar la subida de un archivo
  const handleCancel = (id: string) => {
    cancel.current = id
    delete ref.current[id]
    setNewChange(true)
  }

  // Si se genera un cambio en el ref principal, este cambio se ve reflejado en el state fileStatus (no se pasa directamente ref.current, ya que las referencias no generan callback)
  useEffect(() => {
    if (newChange) {
      setFileStatus(Object.values(ref.current))
      setNewChange(false)
    }
  }, [newChange])

  return (
    <Wrapper shadow={false}>
      {t(`${translationPrefix}.${editing ? 'edit' : 'newV2'}`)}
      <Subtitle $required>{t(`${translationPrefix}.titleV2`)}</Subtitle>
      <Input
        value={data.name}
        onChange={(e) => setData({ ...data, name: e.target.value })}
        placeholder={t(`${translationPrefix}.titlePlaceholder`)}
        autoFocus
      />
      <Subtitle>{t(`${translationPrefix}.descriptionV2`)}</Subtitle>
      <Input.TextArea
        value={data.description}
        onChange={(e) => setData({ ...data, description: e.target.value })}
        placeholder={t(`${translationPrefix}.descriptionPlaceholder`)}
        autoSize={{ minRows: 4, maxRows: 4 }}
      />
      <Subtitle $required>
        {t(`${translationPrefix}.uploadFileV2`)}
        <Tooltip
          title={t(`${translationPrefix}.tootltips.uploadFile`)}
          placement="right"
        >
          <FontAwesomeIcon icon={faInfoCircle} />
        </Tooltip>
      </Subtitle>
      <AwesomeFileInput
        id={'1'}
        files={filesStatus}
        sizeLimit={ONE_GB}
        allowedFiles={[{ extension: 'zip,rar', icon: faFileArchive }]}
        multiFile={false}
        onCancel={handleCancel}
        onRetry={handleRetry}
        onChange={onSelectFiles}
      />
      <Configuration data={data} setData={setData} />
      <Actions>
        <Button
          onClick={() => handleContinue(onCancel)}
          variant={ButtonVariant.Secondary}
          size={ButtonSizes.Small}
        >
          {t(`${translationPrefix}.buttons.cancel`)}
        </Button>
        <Button
          disabled={uploadingScorm}
          size={ButtonSizes.Small}
          onClick={() => handleContinue(handleOnSave)}
        >
          {t(`${translationPrefix}.buttons.save`)}
        </Button>
      </Actions>
      <ConfirmationModal
        show={showConfirmationModal}
        title=""
        text={t('administration.modal.fileLoadCancel')}
        acceptButtonText={t('components.modal.buttons.continue')}
        onAccept={confirmModalAction}
        onClose={() => setShowConfirmationModal(false)}
      />
    </Wrapper>
  )
}

export default Scorm
